import type React from "react";
import {
  type GetThemeValueForKey,
  View,
  type ViewProps,
  XStack,
} from "tamagui";
import type { IconProp } from "./icons";
import { Label } from "./Label";
import { LoadingCircular } from "./LoadingCircular";

export type ButtonType = "primary" | "secondary" | "danger" | "ghost";
export type IconPosition = "before" | "after";

const colors: Record<
  ButtonType,
  {
    color: GetThemeValueForKey<"color">;
    backgroundColor: GetThemeValueForKey<"color">;
  }
> = {
  primary: {
    color: "$pressablePrimaryLabel",
    backgroundColor: "$pressablePrimaryBackground",
  },
  secondary: {
    color: "$color",
    backgroundColor: "$divider",
  },
  ghost: {
    color: "$color",
    backgroundColor: "transparent",
  },
  danger: {
    color: "#fff",
    backgroundColor: "$danger",
  },
};

export const Button: React.FC<
  {
    icon?: IconProp;
    iconPosition?: IconPosition;
    children?: React.ReactNode;
    isLoading?: boolean;
    type?: ButtonType;
  } & ViewProps
> = ({
  children,
  type,
  icon,
  isLoading,
  disabled,
  iconPosition = "after",
  ...viewProps
}) => {
  const Icon = icon;

  const { color, backgroundColor } = colors[type ?? "primary"];

  const isDisabled = disabled || isLoading;

  return (
    <View
      cursor="pointer"
      {...viewProps}
      disabled={isDisabled}
      opacity={disabled ? 0.5 : 1}
      pressStyle={{
        opacity: 0.8,
      }}
    >
      {isLoading && (
        <View
          position="absolute"
          left={"$medium"}
          top={0}
          bottom={0}
          justifyContent="center"
        >
          <LoadingCircular size={30} />
        </View>
      )}
      <XStack
        opacity={isDisabled ? 0.5 : 1}
        flexGrow={1}
        paddingHorizontal="$space.larger"
        paddingVertical="$space.large"
        alignItems="center"
        justifyContent="center"
        backgroundColor={backgroundColor}
        gap="$small"
      >
        {Icon && iconPosition === "before" && (
          <Icon color={color} size={"$size.normal"} />
        )}
        {!!children && (
          <Label numberOfLines={1} color={color}>
            {children}
          </Label>
        )}
        {Icon && iconPosition === "after" && (
          <Icon color={color} size={"$size.normal"} />
        )}
      </XStack>
    </View>
  );
};
